import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Map, Link as LinkIcon, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import { authorizedPost } from "../../../store/actions";
import { GENERATE_GOOGLE_MAPPING, LINK_GOOGLE_MAPPING } from "../../../graphql";

function GoogleMappingSection({ hotelId, colors }) {
  const [generateLoading, setGenerateLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [isLinked, setIsLinked] = useState(false);

  const handleGenerateMapping = async () => {
    setGenerateLoading(true);
    try {
      const res = await authorizedPost(GENERATE_GOOGLE_MAPPING, { hotelId });
      if (res.status) {
        const data = res.data?.generate_google_mapping;
        if (data?.success) {
          toast.success(data.message || "Google mapping generated successfully");
          setIsGenerated(true);
        } else {
          toast.error(data?.message || "Failed to generate Google mapping");
        }
      } else {
        toast.error("Failed to generate Google mapping");
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
    setGenerateLoading(false);
  };

  const handleLinkMapping = async () => {
    setLinkLoading(true);
    try {
      const res = await authorizedPost(LINK_GOOGLE_MAPPING, { hotelId });
      if (res.status) {
        const data = res.data?.link_google_mapping;
        if (data?.success) {
          toast.success(data.message || "Rooms linked to Google successfully");
          setIsLinked(true);
        } else {
          toast.error(data?.message || "Failed to link rooms to Google");
        }
      } else {
        toast.error("Failed to link rooms to Google");
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
    setLinkLoading(false);
  };

  return (
    <Box width="100%" p={3} borderRadius={2} backgroundColor={colors.primary[400]}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Map fontSize="small" />
        <Typography variant="h5" fontWeight={700}>
          Google Hotel Mapping
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          startIcon={
            generateLoading ? <CircularProgress size={18} color="inherit" /> :
            isGenerated ? <CheckCircle /> : <Map />
          }
          onClick={handleGenerateMapping}
          disabled={generateLoading || linkLoading || isGenerated}
          sx={{
            backgroundColor: isGenerated ? colors.grey[600] : colors.greenAccent[600],
            "&:hover": { backgroundColor: isGenerated ? colors.grey[600] : colors.greenAccent[700] },
            fontWeight: 600,
          }}
        >
          {generateLoading ? "Generating..." : isGenerated ? "Mapping Generated" : "Generate Google Mapping"}
        </Button>

        <Button
          variant="contained"
          startIcon={
            linkLoading ? <CircularProgress size={18} color="inherit" /> :
            isLinked ? <CheckCircle /> : <LinkIcon />
          }
          onClick={handleLinkMapping}
          disabled={generateLoading || linkLoading || isLinked}
          sx={{
            backgroundColor: isLinked ? colors.grey[600] : (colors.blueAccent?.[600] || "#3da58a"),
            "&:hover": { backgroundColor: isLinked ? colors.grey[600] : (colors.blueAccent?.[700] || "#2e7c67") },
            fontWeight: 600,
          }}
        >
          {linkLoading ? "Linking..." : isLinked ? "Already Linked" : "Link Mapped Rooms to Google"}
        </Button>

        {isGenerated && (
          <Chip
            label="Mapped"
            size="small"
            sx={{ backgroundColor: colors.greenAccent[600], color: "#fff", fontWeight: 600 }}
          />
        )}
        {isLinked && (
          <Chip
            label="Linked"
            size="small"
            sx={{ backgroundColor: colors.greenAccent[600], color: "#fff", fontWeight: 600 }}
          />
        )}
      </Stack>
    </Box>
  );
}

export default GoogleMappingSection;
